
import React, { memo, Suspense, ErrorInfo, useState, useCallback } from 'react';
import { cn } from "@/lib/utils";
import Header from './Header';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';

interface MainContentProps {
  children: React.ReactNode;
  isMobile: boolean;
  toggleSidebar: () => void;
  showContent: boolean;
}

// Error boundary class component
class MainContentErrorBoundary extends React.Component<
  { children: React.ReactNode, onReset: () => void },
  { hasError: boolean, errorMessage: string }
> {
  constructor(props: { children: React.ReactNode, onReset: () => void }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message || 'An unexpected error occurred' };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Main content error:', error);
    console.error('Error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground text-center mb-4 max-w-md">
            {this.state.errorMessage}
          </p>
          <Button
            onClick={() => {
              this.setState({ hasError: false });
              this.props.onReset();
              toast.success('Application content reloaded');
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reload Content
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

const MainContent: React.FC<MainContentProps> = ({ 
  children, 
  isMobile,
  toggleSidebar,
  showContent
}) => {
  const [key, setKey] = useState(0);
  
  // Force re-render of children by changing the key
  const handleReset = useCallback(() => {
    setKey(prevKey => prevKey + 1);
  }, []);
  
  return (
    <div className={cn(
      "flex-1 flex flex-col h-screen overflow-hidden",
      isMobile ? "w-full" : "ml-64" // Add margin for sidebar when not on mobile
    )}
    role="main"
    aria-busy={!showContent}
    >
      <Header 
        isMobile={isMobile}
        toggleSidebar={toggleSidebar}
      />
      
      <ScrollArea className="flex-1">
        <div className="p-2 sm:p-6 bg-slate-50 dark:bg-slate-900 min-h-[calc(100vh-64px)]">
          <Suspense fallback={<LoadingFallback />}>
            {!showContent && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-slate-200 dark:bg-slate-700 h-10 w-10"></div>
                  <div className="flex-1 space-y-6 py-1">
                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded col-span-2"></div>
                        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded col-span-1"></div>
                      </div>
                      <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showContent && (
              <MainContentErrorBoundary key={key} onReset={handleReset}>
                {children}
              </MainContentErrorBoundary>
            )}
          </Suspense>
        </div>
      </ScrollArea>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(MainContent);
